import React from 'react';
import { Link } from 'react-router-dom';
import { Home, ArrowRight, Phone, MessageCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { WHATSAPP_URL } from '../data/constants';
import { SEOHead } from '../hooks/usePageSEO';

export default function NotFoundPage() {
  const { language } = useLanguage();
  const isFr = language === 'fr';

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center px-4 py-20">
      <SEOHead
        title="Page introuvable | Body-Up Personal Training Nyon"
        description="Cette page n'existe pas. Retournez à l'accueil de Body-Up Personal Training Nyon."
        path="/404"
        noindex={true}
      />

      {/* Decorative background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl" />
      </div>

      <div className="relative text-center max-w-lg mx-auto">
        {/* 404 Number */}
        <div className="text-[140px] sm:text-[180px] font-black leading-none text-transparent bg-clip-text bg-gradient-to-br from-primary via-pink-500 to-primary/30 mb-4 select-none">
          404
        </div>

        {/* Message */}
        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-3">
          {isFr ? 'Page introuvable' : 'Page not found'}
        </h1>
        <p className="text-gray-400 text-base mb-10">
          {isFr
            ? "Oups ! Cette page n'existe pas ou a été déplacée."
            : "Oops! This page doesn't exist or has been moved."}
        </p>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/"
            className="flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-black font-bold px-6 py-3 rounded-xl transition-all duration-300 hover:scale-105"
            data-testid="notfound-home-btn"
          >
            <Home className="w-5 h-5" />
            {isFr ? "Retour à l'accueil" : 'Back to home'}
          </Link>

          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 border border-white/20 hover:border-white/40 text-white font-semibold px-6 py-3 rounded-xl transition-all duration-300 hover:bg-white/5"
            data-testid="notfound-whatsapp-btn"
          >
            <MessageCircle className="w-5 h-5 text-green-400" />
            {isFr ? 'Nous contacter' : 'Contact us'}
          </a>
        </div>

        {/* Quick links */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <p className="text-gray-500 text-sm mb-4">
            {isFr ? 'Pages populaires' : 'Popular pages'}
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            {[
              { to: '/services', label: isFr ? 'Nos services' : 'Our services' },
              { to: '/tarifs', label: isFr ? 'Tarifs' : 'Pricing' },
              { to: '/about', label: isFr ? 'À propos' : 'About' },
              { to: '/contact', label: 'Contact' },
            ].map(link => (
              <Link
                key={link.to}
                to={link.to}
                className="flex items-center gap-1 text-sm text-gray-400 hover:text-white border border-white/10 hover:border-white/30 px-3 py-1.5 rounded-lg transition-all duration-200"
              >
                <ArrowRight className="w-3 h-3" />
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
